"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Marquee = void 0;
const lit_1 = require("lit");
const decorators_js_1 = require("lit/decorators.js");
const repeat_js_1 = require("lit/directives/repeat.js");
const style_map_js_1 = require("lit/directives/style-map.js");
const resize_controller_1 = require("@/controllers/resize-controller");
const schedule_controller_1 = require("@/controllers/schedule-controller");
const shared_styles_1 = require("@/stylesheets/shared.styles");
const utils_1 = require("@/utils");
/**
 * 슬롯 콘텐츠를 가로로 반복 재생하는 marquee.
 * 콘텐츠 폭과 뷰포트 폭을 측정해 필요한 복제 수와 이동 거리를 계산한다.
 */
let Marquee = class Marquee extends lit_1.LitElement {
    constructor() {
        super(...arguments);
        this.direction = 'left';
        this.gap = '4';
        this.speed = 80;
        this.pauseOnHover = false;
        this.copyCount = 1;
        this.distance = 0;
        this.duration = 1;
        this.measureFrame = new schedule_controller_1.ScheduleController(this, () => this.measure());
        this.resize = new resize_controller_1.ResizeController(this, {
            getTargets: () => [this, this.sourceElement],
            onResize: () => this.measureFrame.request(),
        });
        this.handleSlotChange = () => {
            this.measureFrame.request();
            this.updateComplete.then(() => this.syncClones());
        };
    }
    render() {
        const cloneIndexes = Array.from({ length: this.copyCount - 1 }, (_, index) => index);
        const trackStyle = {
            '--marquee-distance': `${this.distance}px`,
            '--marquee-duration': `${this.duration}s`,
        };
        return (0, lit_1.html) `
      <div class="viewport">
        <div class="track" style=${(0, style_map_js_1.styleMap)(trackStyle)}>
          <div class="group source">
            <slot @slotchange=${this.handleSlotChange}></slot>
          </div>
          ${(0, repeat_js_1.repeat)(cloneIndexes, index => index, () => (0, lit_1.html) `
              <div class="group clone" aria-hidden="true" inert></div>
            `)}
        </div>
      </div>
    `;
    }
    connectedCallback() {
        super.connectedCallback();
        this.setAttribute('role', 'marquee');
    }
    updated(changed) {
        if (changed.has('gap'))
            this.measureFrame.request();
        if (changed.has('height'))
            this.updateHeight();
        if (changed.has('speed'))
            this.measureFrame.request();
        if (changed.has('copyCount'))
            this.syncClones();
    }
    measure() {
        const source = this.sourceElement;
        if (!source)
            return;
        const distance = Math.ceil(source.getBoundingClientRect().width);
        if (distance <= 0)
            return;
        const viewportWidth = Math.ceil(this.getBoundingClientRect().width);
        const copyCount = Math.max(2, Math.ceil((viewportWidth + distance) / distance) + 1);
        const duration = Math.max(1, distance / Math.max(1, this.speed));
        this.distance = distance;
        this.copyCount = copyCount;
        this.duration = duration;
    }
    syncClones() {
        const nodes = this.assignedNodes();
        const clones = this.renderRoot.querySelectorAll('.clone');
        clones.forEach(container => {
            container.replaceChildren(...nodes.map(node => node.cloneNode(true)));
        });
    }
    assignedNodes() {
        return (this.slotElement?.assignedNodes({ flatten: true }).filter(node => {
            return node.nodeType !== Node.TEXT_NODE || Boolean(node.textContent?.trim());
        }) ?? []);
    }
    // 임의 값이라 나열할 수 없고 host가 소비하므로 host에 직접 주입한다.
    updateHeight() {
        if (!this.height) {
            this.style.removeProperty('--marquee-height');
            return;
        }
        this.style.setProperty('--marquee-height', this.height);
    }
};
Marquee.styles = (0, lit_1.css) `
    :host {
      display: block;
      max-width: 100%;
      overflow: hidden;
      --marquee-gap: var(--space-4);
      --marquee-height: auto;
    }

    ${(0, lit_1.unsafeCSS)((0, utils_1.buildAttributeRules)('gap', (0, shared_styles_1.spaceTokens)('--marquee-gap')))}

    .viewport {
      width: 100%;
      height: var(--marquee-height);
      overflow: hidden;
    }

    .track {
      display: flex;
      width: max-content;
      height: 100%;
      animation: marquee-scroll var(--marquee-duration) linear infinite;
      transform: translate3d(0, 0, 0);
      will-change: transform;
    }

    :host([direction='right']) .track {
      animation-name: marquee-scroll-right;
    }

    :host([pause-on-hover]:hover) .track {
      animation-play-state: paused;
    }

    .group {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      gap: var(--marquee-gap);
      height: 100%;
      padding-inline-end: var(--marquee-gap);
      box-sizing: border-box;
    }

    ::slotted(*) {
      flex: 0 0 auto;
    }

    @media (prefers-reduced-motion: reduce) {
      .track {
        animation: none;
      }
    }

    @keyframes marquee-scroll {
      to {
        transform: translate3d(calc(var(--marquee-distance) * -1), 0, 0);
      }
    }

    @keyframes marquee-scroll-right {
      from {
        transform: translate3d(calc(var(--marquee-distance) * -1), 0, 0);
      }

      to {
        transform: translate3d(0, 0, 0);
      }
    }
  `;
__decorate([
    (0, decorators_js_1.property)({ type: String, reflect: true })
], Marquee.prototype, "direction", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String, reflect: true })
], Marquee.prototype, "gap", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String })
], Marquee.prototype, "height", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: Number })
], Marquee.prototype, "speed", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: Boolean, reflect: true, attribute: 'pause-on-hover' })
], Marquee.prototype, "pauseOnHover", void 0);
__decorate([
    (0, decorators_js_1.state)()
], Marquee.prototype, "copyCount", void 0);
__decorate([
    (0, decorators_js_1.state)()
], Marquee.prototype, "distance", void 0);
__decorate([
    (0, decorators_js_1.state)()
], Marquee.prototype, "duration", void 0);
__decorate([
    (0, decorators_js_1.query)('.source')
], Marquee.prototype, "sourceElement", void 0);
__decorate([
    (0, decorators_js_1.query)('slot')
], Marquee.prototype, "slotElement", void 0);
Marquee = __decorate([
    (0, decorators_js_1.customElement)('mm-marquee')
], Marquee);
exports.Marquee = Marquee;
//# sourceMappingURL=data:application/json;base64,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